import { createContext, use } from 'react'

export interface ConfirmRequest {
    title: string
    /** Say what will happen and to how many records. Operators read this one. */
    description?: string
    confirmLabel?: string
    cancelLabel?: string
    /** Tints the confirm button and the icon. Use for anything that moves money. */
    tone?: 'default' | 'destructive'
    /**
     * Requires the operator to type this exact string first. Reserve it for the
     * irreversible ones -- refunds, merges, mass deletes -- or it becomes noise
     * people learn to type without reading.
     */
    requireTyping?: string
}

export type ConfirmFn = (request: ConfirmRequest) => Promise<boolean>

export const ConfirmContext = createContext<ConfirmFn | null>(null)

/**
 * Replaces the eight native `confirm()` calls in the legacy app, which could not be
 * styled, could not explain themselves, blocked the main thread, and are suppressible
 * by the browser after repeated use -- meaning a destructive action could silently
 * proceed without asking.
 *
 * @example
 * if (!(await confirm({ title: 'Refund 12 withdrawals?', tone: 'destructive' }))) return
 */
export function useConfirm(): ConfirmFn {
    const confirm = use(ConfirmContext)
    if (!confirm) throw new Error('useConfirm() requires <ConfirmProvider> above it in the tree')
    return confirm
}
